import { defineRouting } from 'next-intl/routing'
import { BUILTIN_LOCALES, DEFAULT_LOCALE, LOCALE_COOKIE } from '@hamishon/i18n'

/**
 * URL strategy.
 *
 * `as-needed` keeps Persian prefix-free — `/orders/123`, exactly the URLs
 * Google has already indexed — while every other locale is namespaced under
 * its code (`/en/orders/123`). Changing this would invalidate the whole
 * existing search index, so treat it as fixed.
 *
 * `locales` only lists the built-in pair. Locales an admin adds at runtime
 * are handled by the middleware, which rebuilds this config from the
 * `languages` table (Phase 3) — `defineRouting` needs a static array and
 * cannot be the source of truth for something the database owns.
 */
export const routing = defineRouting({
  locales: [...BUILTIN_LOCALES],
  defaultLocale: DEFAULT_LOCALE,
  localePrefix: 'as-needed',
  localeCookie: {
    name: LOCALE_COOKIE,
    // A year. Without an explicit maxAge next-intl writes a session cookie,
    // so a returning visitor who chose English would land back on Persian
    // after closing the browser — the exact thing this feature exists to fix.
    maxAge: 60 * 60 * 24 * 365,
    // Cast because next-intl v4 derives this type via
    // `Pick<Partial<ResponseCookie>, … | 'partitioned' | …>`, and Next 14.0.4's
    // ResponseCookie has no `partitioned` key (it arrived in Next 15), which
    // collapses the whole attribute type. The runtime spreads this object
    // verbatim, so the values above take effect regardless. Remove the cast
    // once the app is on Next 15+.
  } as unknown as boolean,
})
